import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Eye, Clock, Heart, TrendingUp, LineChart, Trophy, LayoutGrid, Users2, CalendarCheck, Link2Off, Youtube } from 'lucide-react';

const SUMMARY_TILES = [
  { key: 'views', label: 'Total Views', icon: Eye },
  { key: 'watch', label: 'Watch Time', icon: Clock },
  { key: 'engagement', label: 'Engagement Rate', icon: Heart },
  { key: 'growth', label: 'Audience Growth', icon: TrendingUp },
];

const DETAIL_SECTIONS = [
  { key: 'trends', label: 'Performance Trends Over Time', icon: LineChart, body: 'Once you have published content with tracked metrics, a trend line will appear here.' },
  { key: 'best', label: 'Best-Performing Content', icon: Trophy, body: 'Your top content by views and engagement will be ranked here.' },
  { key: 'format', label: 'Performance by Content Format', icon: LayoutGrid, body: 'Compare how long-form, short-form, and image posts perform relative to each other.' },
  { key: 'audienceEngagement', label: 'Audience Engagement', icon: Users2, body: 'Likes, comments, shares, and saves broken down over time will appear here.' },
  { key: 'consistency', label: 'Publishing Consistency', icon: CalendarCheck, body: 'How regularly you publish compared to your preferred posting schedule.' },
];

/** Performance Analytics detail dialog — reuses the same empty-state
 * language as the page-level Performance Overview / Platform Breakdown /
 * Content Performance sections. No real performance pipeline exists yet,
 * so every section stays an honest empty state rather than invented numbers. */
export default function PerformanceAnalyticsDialog({ open, onOpenChange, connectedPlatforms, timeframe }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="performance-analytics-dialog">
        <DialogHeader><DialogTitle className="text-white">Performance Analytics</DialogTitle></DialogHeader>
        <p className="text-xs text-[#8A93A6] -mt-3">Showing: {timeframe === 'all' ? 'All Time' : timeframe}</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {SUMMARY_TILES.map(t => (
            <Card key={t.key} className="glass border-white/5" data-testid={`perf-summary-${t.key}`}>
              <CardContent className="pt-4 pb-4 text-center">
                <t.icon className="h-4 w-4 text-[#3A7BFF] mx-auto mb-1.5" />
                <p className="text-lg font-bold text-[#555E6E]">No data yet</p>
                <p className="text-[10px] text-[#8A93A6] mt-0.5">{t.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {connectedPlatforms.length === 0 && (
          <p className="text-xs text-[#555E6E] text-center">Connect a platform in Account → Creator Profile to start tracking real performance.</p>
        )}

        {/* Platform Breakdown — reused from the existing widget */}
        <div>
          <p className="text-xs text-[#8A93A6] uppercase tracking-wider mb-2">Platform Breakdown</p>
          <Card className="glass border-white/5">
            <CardContent className="pt-5 pb-5">
              {connectedPlatforms.length === 0 ? (
                <div className="text-center py-6">
                  <Link2Off className="h-8 w-8 text-[#8A93A6] mx-auto mb-2" />
                  <p className="text-[#8A93A6] text-sm">No platforms connected</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="perf-platform-breakdown">
                  {connectedPlatforms.includes('youtube') && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-sm text-white"><Youtube className="h-4 w-4 text-red-500" />YouTube</span><span className="text-xs text-[#8A93A6]">-- views</span></div>
                      <div className="h-1.5 bg-[#1E2029] rounded-full overflow-hidden"><div className="h-full bg-red-500" style={{ width: '0%' }} /></div>
                    </div>
                  )}
                  {connectedPlatforms.includes('tiktok') && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-sm text-white"><div className="h-4 w-4 rounded bg-gradient-to-br from-cyan-400 to-pink-500" />TikTok</span><span className="text-xs text-[#8A93A6]">-- views</span></div>
                      <div className="h-1.5 bg-[#1E2029] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-400 to-pink-500" style={{ width: '0%' }} /></div>
                    </div>
                  )}
                  {connectedPlatforms.includes('instagram') && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-sm text-white"><div className="h-4 w-4 rounded bg-gradient-to-br from-purple-500 to-pink-500" />Instagram</span><span className="text-xs text-[#8A93A6]">-- views</span></div>
                      <div className="h-1.5 bg-[#1E2029] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-purple-500 to-pink-500" style={{ width: '0%' }} /></div>
                    </div>
                  )}
                  {connectedPlatforms.includes('facebook') && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-sm text-white"><div className="h-4 w-4 rounded bg-blue-600" />Facebook</span><span className="text-xs text-[#8A93A6]">-- views</span></div>
                      <div className="h-1.5 bg-[#1E2029] rounded-full overflow-hidden"><div className="h-full bg-blue-600" style={{ width: '0%' }} /></div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Accordion type="single" collapsible className="border border-white/10 rounded-lg px-3" data-testid="perf-detail-accordion">
          {DETAIL_SECTIONS.map(s => (
            <AccordionItem key={s.key} value={s.key} className="border-white/10">
              <AccordionTrigger className="text-white text-sm hover:no-underline" data-testid={`perf-detail-trigger-${s.key}`}>
                <span className="flex items-center gap-2"><s.icon className="h-4 w-4 text-[#8A93A6]" />{s.label}</span>
              </AccordionTrigger>
              <AccordionContent className="text-xs text-[#8A93A6]">{s.body}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </DialogContent>
    </Dialog>
  );
}
